import type { TargetareAmbiguitateServiciu } from "@/types/targetare";
import { ETICHETE_AMBIGUITATE } from "@/utils/targetareConfig";
import {
  raportImplicit,
  type DefinitieRaport,
  type FiltruRapidDef,
} from "@/utils/targetareRaport";
import type { ColoanaTabel } from "@/utils/targetareTabel";

export type RandAmbiguitate = TargetareAmbiguitateServiciu & {
  cheie: string;
  type_text: string;
  regula_text: string;
  sector_text: string;
  anexa_text: string;
};

export function randuriAmbiguitate(
  servicii: TargetareAmbiguitateServiciu[]
): RandAmbiguitate[] {
  return servicii.map((s) => ({
    ...s,
    cheie: s.entity_id,
    type_text: s.type ?? "(fără)",
    regula_text: s.regula_tip ?? "",
    sector_text: s.sector ?? "(fără sector)",
    anexa_text: s.anexa ?? "",
  }));
}

export const COLOANE_AMBIGUITATE: ColoanaTabel<RandAmbiguitate>[] = [
  {
    id: "sector_text",
    eticheta: "Sector",
    tip: "lista",
    latime: 140,
    implicit: true,
  },
  {
    id: "name",
    eticheta: "Serviciu NIS2",
    tip: "text",
    latime: 280,
    implicit: true,
  },
  { id: "entity_id", eticheta: "Id", tip: "text", latime: 80, implicit: true },
  {
    id: "anexa_text",
    eticheta: "Anexă",
    tip: "lista",
    latime: 70,
    implicit: false,
  },
  {
    id: "type_text",
    eticheta: "Ambiguitate",
    tip: "lista",
    latime: 150,
    implicit: true,
    optiuni: ["Neambiguu", "Ambiguu", "Ierarhic", "N/A", "(fără)"],
    etichete: ETICHETE_AMBIGUITATE,
  },
  {
    id: "super_ambiguu",
    eticheta: "Super-ambiguu",
    tip: "bool",
    latime: 110,
    implicit: true,
  },
  {
    id: "scor_principal",
    eticheta: "Scor CAEN singur",
    tip: "numar",
    latime: 100,
    implicit: true,
  },
  {
    id: "scor_lista_oficiala",
    eticheta: "Scor CAEN + listă oficială",
    tip: "numar",
    latime: 110,
    implicit: false,
  },
  {
    id: "nr_coduri",
    eticheta: "Coduri principale",
    tip: "numar",
    latime: 90,
    implicit: true,
  },
  {
    id: "nr_coduri_secundare",
    eticheta: "Coduri secundare",
    tip: "numar",
    latime: 90,
    implicit: false,
  },
  {
    id: "firme_principal",
    eticheta: "Firme prin CAEN principal",
    tip: "numar",
    latime: 110,
    implicit: true,
  },
  {
    id: "firme_principal_scop",
    eticheta: "… în scop",
    tip: "numar",
    latime: 90,
    implicit: true,
  },
  {
    id: "firme_secundar",
    eticheta: "Firme prin cod autorizat",
    tip: "numar",
    latime: 110,
    implicit: false,
  },
  {
    id: "firme_secundar_scop",
    eticheta: "… în scop",
    tip: "numar",
    latime: 90,
    implicit: false,
  },
  {
    id: "firme_lista",
    eticheta: "Firme de pe liste",
    tip: "numar",
    latime: 100,
    implicit: true,
  },
  {
    id: "firme_lista_scop",
    eticheta: "… în scop",
    tip: "numar",
    latime: 90,
    implicit: true,
  },
  {
    id: "justificare",
    eticheta: "Justificare",
    tip: "text",
    latime: 320,
    implicit: false,
  },
];

export const FILTRE_RAPIDE_AMBIGUITATE: FiltruRapidDef[] = [
  { coloana: "sector_text", eticheta: "Sector" },
  {
    coloana: "type_text",
    eticheta: "Ambiguitate",
    optiuni: ["Neambiguu", "Ambiguu", "Ierarhic", "N/A", "(fără)"],
    etichete: ETICHETE_AMBIGUITATE,
  },
  {
    coloana: "super_ambiguu",
    eticheta: "Super-ambiguu",
    optiuni: ["da", "nu"],
  },
  { coloana: "anexa_text", eticheta: "Anexă" },
];

const IMPLICITE = COLOANE_AMBIGUITATE.filter((c) => c.implicit).map(
  (c) => c.id
);

export const definitieRaportAmbiguitate: DefinitieRaport<RandAmbiguitate> = {
  cheie: "targetare.rapoarte.ambiguitate",
  coloane: COLOANE_AMBIGUITATE,
  rapoarteImplicite: [
    raportImplicit("Toate serviciile", IMPLICITE, {
      sortare: [{ id: "sector_text", desc: false }],
    }),
    raportImplicit("Servicii ambigue sau super-ambigue", IMPLICITE, {
      filtre: [
        {
          id: "f-amb",
          coloana: "type_text",
          operator: "in",
          valori: ["Ambiguu", "Ierarhic"],
          activ: true,
        },
      ],
    }),
    raportImplicit("Servicii fără cod CAEN", IMPLICITE, {
      filtre: [
        {
          id: "f-na",
          coloana: "type_text",
          operator: "in",
          valori: ["N/A", "(fără)"],
          activ: true,
        },
      ],
    }),
  ],
  cheieRand: (r) => r.cheie,
  filtreRapide: FILTRE_RAPIDE_AMBIGUITATE,
  numeFisier: "ambiguitate-servicii",
  mesajGol: "Serviciile nu au putut fi încărcate.",
};
